import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import GroupManager from './pages/GroupManager';
import GroupHome from './pages/GroupHome';
import SurveyWizard from './pages/SurveyWizard';
import VerifyMagicLink from './pages/VerifyMagicLink';
import ShortLink from './pages/ShortLink';
import NotificationSettings from './pages/NotificationSettings';
import { PrivacyPolicy, Terms } from './pages/Legal';
import { authService } from './services/auth';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#9f2d3a', light: '#c4505a', dark: '#7c1f2a' },
    secondary: { main: '#1f4d3a', light: '#3d6b56', dark: '#143528' },
    background: { default: '#f3efe6', paper: '#fffdf8' },
    text: { primary: '#2a2420', secondary: '#5c534c' },
    divider: '#e7dfd2',
  },
  typography: {
    fontFamily: '"Source Sans 3", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Mountains of Christmas", cursive', fontWeight: 700 },
    h2: { fontFamily: '"Mountains of Christmas", cursive', fontWeight: 700 },
    button: { fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', boxShadow: 'none' } } },
    MuiCard: {
      styleOverrides: {
        root: { border: '1px solid #e7dfd2', boxShadow: 'none', backgroundImage: 'none' },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiAppBar: { styleOverrides: { root: { boxShadow: 'none' } } },
  },
});

/** Requires sign-in; sends unauthenticated visitors to the landing page and back afterwards. */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  if (authService.isAuthenticated()) return <>{children}</>;
  const redirect = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/?redirect=${redirect}`} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/verify" element={<VerifyMagicLink />} />
          <Route path="/s/:code" element={<ShortLink />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/g/:groupId" element={<ProtectedRoute><GroupHome /></ProtectedRoute>} />
          <Route path="/group/:groupId" element={<ProtectedRoute><GroupManager /></ProtectedRoute>} />
          <Route path="/survey/:groupId/:participantId" element={<ProtectedRoute><SurveyWizard /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          {/* Legacy links from old emails */}
          <Route path="/match" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
